/**
 * Service data models with Zod schemas
 * Explicit schema definitions with parsing validation
 */
import { parseISO } from "date-fns";
import { z } from "zod";

// Domain schema (camelCase)
export const ServiceSchema = z.object({
  id: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  businessId: z.string(),
  sortOrder: z.number().int().nonnegative(),
  name: z.string(),
  description: z.string().nullable(),
  basePrice: z.number().nullable(),
  bookingFee: z.number().nullable(),
  durationMinutes: z.number().nullable(),
  bufferBeforeMinutes: z.number().nullable(),
  bufferAfterMinutes: z.number().nullable(),
  isActive: z.boolean(),
  settings: z.any().nullable(),
  createdBy: z.string().nullable(),
  color: z.string().nullable(),
  imgSrc: z.string().nullable(),
});

const CreateServiceRequestSchema = ServiceSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  createdBy: true,
  sortOrder: true,
}).partial({
  description: true,
  basePrice: true,
  bookingFee: true,
  durationMinutes: true,
  bufferBeforeMinutes: true,
  bufferAfterMinutes: true,
  isActive: true,
  settings: true,
  color: true,
  imgSrc: true,
});

const UpdateServiceRequestSchema = ServiceSchema.omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
  businessId: true,
  createdBy: true,
  sortOrder: true,
}).partial();

// DTO schema (snake_case)
export const ServiceDtoSchema = z.object({
  id: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  business_id: z.string(),
  sort_order: z.number().int().nonnegative(),
  name: z.string(),
  description: z.string().nullable(),
  base_price: z.number().nullable(),
  booking_fee: z.number().nullable(),
  duration_minutes: z.number().nullable(),
  buffer_before_minutes: z.number().nullable(),
  buffer_after_minutes: z.number().nullable(),
  is_active: z.boolean(),
  settings: z.any().nullable(),
  created_by: z.string().nullable(),
  color: z.string().nullable(),
  img_src: z.string().nullable(),
});

export const CreateServiceDtoSchema = ServiceDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  created_by: true,
  sort_order: true,
}).partial({
  description: true,
  base_price: true,
  booking_fee: true,
  duration_minutes: true,
  buffer_before_minutes: true,
  buffer_after_minutes: true,
  is_active: true,
  settings: true,
  color: true,
  img_src: true,
});

export const UpdateServiceDtoSchema = ServiceDtoSchema.omit({
  id: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  created_by: true,
  sort_order: true,
}).partial();

const ReorderServicesRequestSchema = z.object({
  businessId: z.string().min(1),
  serviceIds: z.array(z.string().min(1)),
});

export const ReorderServicesDtoSchema = z.object({
  service_ids: z.array(z.string().min(1)),
});

// Infer DTO types
export type ServiceDto = z.infer<typeof ServiceDtoSchema>;
export type CreateServiceDto = z.infer<typeof CreateServiceDtoSchema>;
export type UpdateServiceDto = z.infer<typeof UpdateServiceDtoSchema>;
export type ReorderServicesDto = z.infer<typeof ReorderServicesDtoSchema>;

// Infer domain types
export type Service = z.infer<typeof ServiceSchema>;
export type CreateServiceRequest = z.infer<typeof CreateServiceRequestSchema>;
export type UpdateServiceRequest = z.infer<typeof UpdateServiceRequestSchema>;
export type ReorderServicesRequest = z.infer<typeof ReorderServicesRequestSchema>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromServiceDtoToService(dto: ServiceDto): Service {
  const serviceDto = ServiceDtoSchema.parse(dto);

  return {
    id: serviceDto.id,
    createdAt: parseISO(serviceDto.created_at),
    modifiedAt: parseISO(serviceDto.modified_at),
    businessId: serviceDto.business_id,
    sortOrder: serviceDto.sort_order,
    name: serviceDto.name,
    description: serviceDto.description,
    basePrice: serviceDto.base_price,
    bookingFee: serviceDto.booking_fee,
    durationMinutes: serviceDto.duration_minutes,
    bufferBeforeMinutes: serviceDto.buffer_before_minutes,
    bufferAfterMinutes: serviceDto.buffer_after_minutes,
    isActive: serviceDto.is_active,
    settings: serviceDto.settings,
    createdBy: serviceDto.created_by,
    color: serviceDto.color,
    imgSrc: serviceDto.img_src,
  };
}

export function fromReorderServicesRequestToReorderServicesDto(
  request: ReorderServicesRequest
): ReorderServicesDto {
  const reorderServicesRequest = ReorderServicesRequestSchema.parse(request);
  return { service_ids: reorderServicesRequest.serviceIds };
}

export function fromCreateServiceRequestToCreateServiceDto(
  request: CreateServiceRequest
): CreateServiceDto {
  const createServiceRequest = CreateServiceRequestSchema.parse(request);

  return {
    business_id: createServiceRequest.businessId,
    name: createServiceRequest.name,
    description: createServiceRequest.description,
    base_price: createServiceRequest.basePrice,
    booking_fee: createServiceRequest.bookingFee,
    duration_minutes: createServiceRequest.durationMinutes,
    buffer_before_minutes: createServiceRequest.bufferBeforeMinutes,
    buffer_after_minutes: createServiceRequest.bufferAfterMinutes,
    is_active: createServiceRequest.isActive,
    settings: createServiceRequest.settings,
    color: createServiceRequest.color,
    img_src: createServiceRequest.imgSrc,
  };
}

export function fromUpdateServiceRequestToUpdateServiceDto(
  request: UpdateServiceRequest
): UpdateServiceDto {
  const updateServiceRequest = UpdateServiceRequestSchema.parse(request);

  return {
    name: updateServiceRequest.name,
    description: updateServiceRequest.description,
    base_price: updateServiceRequest.basePrice,
    booking_fee: updateServiceRequest.bookingFee,
    duration_minutes: updateServiceRequest.durationMinutes,
    buffer_before_minutes: updateServiceRequest.bufferBeforeMinutes,
    buffer_after_minutes: updateServiceRequest.bufferAfterMinutes,
    is_active: updateServiceRequest.isActive,
    settings: updateServiceRequest.settings,
    color: updateServiceRequest.color,
    img_src: updateServiceRequest.imgSrc,
  };
}
